import React, { useState } from 'react';
import { MergeInfoInDTO } from '@api/ringil-api.v2.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { MergeConflict, MergeSummary } from '@app/pages/expedition/create/mappers/mergeToForm.ts';
import { MergedRoute } from '@app/pages/expedition/create/mergeRules.ts';
import { MergeSource } from '@app/pages/expedition/create/mergeEligibility.ts';
import MergeSummaryList from '@app/pages/expedition/create/components/MergeSummaryList.tsx';
import type { ConsolidationOrigin } from '@app/pages/expedition/create/hooks/useExpeditionPrefill.ts';

interface ComponentProps {
  visible: boolean;
  sources: MergeSource[];
  summary: MergeSummary;
  /** Repeated here because this is the last point at which the merge can still be called off. */
  conflicts: MergeConflict[];
  keptApart: MergedRoute['keptApart'];
  contactConflicts: MergedRoute['contactConflicts'];
  timeslotConflicts?: MergedRoute['timeslotConflicts'];
  /** Set when the merge is a route-optimization group; it is referenced in the merged notifications' audit. */
  consolidation?: ConsolidationOrigin;
  /** Whether any source actually has a reservation to archive. */
  hasLinkedReservations: boolean;
  /** Whether any source actually came from a supplier notification. */
  hasLinkedSupplierNotifications: boolean;
  loading: boolean;
  onHide: () => void;
  onConfirm: (mergeInfo: MergeInfoInDTO) => void;
}

/**
 * Last stop before the merge happens: says which shipments are about to be closed and asks what
 * should follow from that.
 *
 * Reservation and supplier-notification archiving are only offered when a source actually has one —
 * shown disabled rather than hidden, so it is clear the question was considered and not simply
 * missing.
 */
const MergeConfirmDialog: React.FC<ComponentProps> = ({
  visible,
  sources,
  summary,
  conflicts,
  keptApart,
  contactConflicts,
  timeslotConflicts = [],
  consolidation,
  hasLinkedReservations,
  hasLinkedSupplierNotifications,
  loading,
  onHide,
  onConfirm,
}) => {
  const { tr } = useTranslationLgs();
  const [sendCancelEmails, setSendCancelEmails] = useState(true);
  const [archiveReservations, setArchiveReservations] = useState(true);
  // Off by default: archiving the source notifications is the more consequential of the two, and
  // unlike the reservations they are not released by the merge itself.
  const [archiveSupplierNotifications, setArchiveSupplierNotifications] = useState(false);

  // One line per thing left undecided; the detail is in the panel behind the dialog, and repeating
  // it in full here would bury the question actually being asked.
  const unresolved = [
    conflicts.length > 0 && tr('CreateExpedition.mergeConfirmConflicts', '{{count}} fields differed and were left empty', { count: conflicts.length }),
    contactConflicts.length > 0 &&
      tr('CreateExpedition.mergeConfirmContactConflicts', '{{count}} contact details differed and were left empty', { count: contactConflicts.length }),
    keptApart.length > 0 && tr('CreateExpedition.mergeConfirmKeptApart', '{{count}} stops at the same place could not be combined', { count: keptApart.length }),
    timeslotConflicts.length > 0 &&
      tr('CreateExpedition.mergeConfirmTimeslotConflicts', '{{count}} stops combine time windows that do not overlap', { count: timeslotConflicts.length }),
  ].filter(Boolean) as string[];

  return (
    <RingilDialog
      visible={visible}
      onHide={onHide}
      heading={tr('CreateExpedition.mergeConfirmHeading', 'Create the merged shipment?')}
      buttons={
        <div className={'flex flex-row gap-4'}>
          <RingilButton mode={'outlined'} label={tr('CreateExpedition.mergeConfirmCancel', 'Back')} onClick={onHide} dataTestId={'mergeConfirmCancel'} />
          <RingilButton
            mode={'filled'}
            label={tr('CreateExpedition.mergeConfirmSubmit', 'Create and close the originals')}
            loading={loading}
            onClick={() =>
              onConfirm({
                mergedExpeditionIds: sources.filter(source => source.kind === 'expedition').map(source => source.id),
                mergedSupplierNotificationIds: sources.filter(source => source.kind === 'supplierNotification').map(source => source.id),
                sendCancelEmails,
                // Nothing to archive when no source carries a reservation.
                archiveReservations: hasLinkedReservations && archiveReservations,
                // Likewise for the supplier notifications behind the closed shipments.
                archiveSupplierNotifications: hasLinkedSupplierNotifications && archiveSupplierNotifications,
                routeConsolidation: consolidation
                  ? { requestId: consolidation.requestId, groupId: consolidation.groupId, modelVersion: consolidation.modelVersion }
                  : undefined,
              })
            }
            dataTestId={'mergeConfirmSubmit'}
          />
        </div>
      }
    >
      <div className={'flex flex-col gap-4'}>
        <span>{tr('CreateExpedition.mergeConfirmExplanation', 'A new shipment will be created and these shipments will be closed:')}</span>

        <ul className={'m-0 flex list-disc flex-col gap-1 pl-4'} data-testid={'mergeConfirmSources'}>
          {sources.map(source => (
            <li key={source.id}>
              {source.applicationId || source.id}
              {source.kind === 'supplierNotification' && ` — ${tr('CreateExpedition.mergeSourceSupplierNotification', 'Supplier notification')}`}
            </li>
          ))}
        </ul>

        {sources.some(source => source.kind === 'supplierNotification') && (
          <COInlineMessage variant={'alert'} dataTestId={'mergeConfirmSupplierNotifications'}>
            {tr(
              'CreateExpedition.mergeConfirmSupplierNotifications',
              'Any of these supplier notifications that already has a shipment of its own will have that shipment closed too, even though it is not listed above.',
            )}
          </COInlineMessage>
        )}

        <COInlineMessage variant={'info'} dataTestId={'mergeConfirmSummary'}>
          <div className={'flex flex-col gap-1'}>
            <strong>{tr('CreateExpedition.mergeSummaryHeading', 'What this merge does')}</strong>
            <MergeSummaryList summary={summary} />
          </div>
        </COInlineMessage>

        {unresolved.length > 0 && (
          <COInlineMessage variant={'alert'} dataTestId={'mergeConfirmUnresolved'}>
            <div className={'flex flex-col gap-1'}>
              <strong>{tr('CreateExpedition.mergeConfirmUnresolvedHeading', 'Left for you to sort out afterwards')}</strong>
              <ul className={'m-0 flex list-disc flex-col gap-1 pl-4'}>
                {unresolved.map(entry => (
                  <li key={entry}>{entry}</li>
                ))}
              </ul>
            </div>
          </COInlineMessage>
        )}

        <InputCheckbox
          value={sendCancelEmails}
          label={tr('CreateExpedition.mergeSendCancelEmails', 'Send cancellation e-mails')}
          note={tr('CreateExpedition.mergeSendCancelEmailsNote', 'Notifies the people following the closed shipments.')}
          onChange={(_, value) => setSendCancelEmails(value)}
        />

        <InputCheckbox
          value={hasLinkedReservations && archiveReservations}
          disabled={!hasLinkedReservations}
          label={tr('CreateExpedition.mergeArchiveReservations', 'Archive linked reservations')}
          note={
            hasLinkedReservations
              ? tr('CreateExpedition.mergeArchiveReservationsNote', 'The time slots booked for the closed shipments will be released.')
              : tr('CreateExpedition.mergeArchiveReservationsNone', 'None of these shipments has a reservation.')
          }
          onChange={(_, value) => setArchiveReservations(value)}
        />

        <InputCheckbox
          value={hasLinkedSupplierNotifications && archiveSupplierNotifications}
          disabled={!hasLinkedSupplierNotifications}
          label={tr('CreateExpedition.mergeArchiveSupplierNotifications', 'Archive linked supplier notifications')}
          note={
            hasLinkedSupplierNotifications
              ? tr('CreateExpedition.mergeArchiveSupplierNotificationsNote', 'The supplier notifications the closed shipments were created from will be archived.')
              : tr('CreateExpedition.mergeArchiveSupplierNotificationsNone', 'None of these shipments comes from a supplier notification.')
          }
          onChange={(_, value) => setArchiveSupplierNotifications(value)}
        />
      </div>
    </RingilDialog>
  );
};

export default MergeConfirmDialog;
